import { Search, X } from 'lucide-react';
import { useState, useMemo, useId } from 'react';

interface SearchInputProps {
  /** Forwarded to the native input so an external <label htmlFor> can reference it. */
  id?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  /** Accessible name for the clear button — set it when a page renders several SearchInputs. */
  clearLabel?: string;
  /** Optional validation function. Returns error message if invalid, null if valid. */
  validate?: (value: string) => string | null;
}

export default function SearchInput({
  id,
  placeholder = 'Search...',
  value,
  onChange,
  onClear,
  clearLabel = 'Clear search',
  validate,
}: SearchInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const errorId = useId();

  const errorMessage = useMemo(() => {
    if (!validate || !value.trim()) return null;
    return validate(value.trim());
  }, [validate, value]);

  const hasError = !!errorMessage;

  return (
    <div className="flex flex-col gap-1">
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-md bg-background border transition-colors ${
          hasError ? 'border-destructive' : isFocused ? 'border-primary' : 'border-border'
        }`}
      >
        <Search className={`h-4 w-4 ${hasError ? 'text-destructive' : 'text-muted-foreground'}`} />
        <input
          id={id}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? errorId : undefined}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange('');
              onClear?.();
            }}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label={clearLabel}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {hasError && (
        <span id={errorId} className="text-xs text-destructive">{errorMessage}</span>
      )}
    </div>
  );
}
